import { addProtocolToUrl } from './urlHelpers';

export const FINDEM_CALIBRATION_AGENT_ID = import.meta.env.VITE_FINDEM_CALIBRATION_AGENT_ID;
export const FINDEM_BOOST_AGENT_ID = import.meta.env.VITE_FINDEM_BOOST_AGENT_ID;

export const findemWidgetEventNames = {
  FINDEM_WIDGET_READY: 'FINDEM_WIDGET_READY',
  FINDEM_WIDGET_OPENED: 'FINDEM_WIDGET_OPENED',
  FINDEM_WIDGET_ERROR: 'FINDEM_WIDGET_ERROR',
  FINDEM_WIDGET_MESSAGE: 'FINDEM_WIDGET_MESSAGE',
  FINDEM_WIDGET_LOADED: 'FINDEM_WIDGET_LOADED',
  FINDEM_WIDGET_SESSION_CHANGE: 'FINDEM_WIDGET_SESSION_CHANGE',
  FINDEM_WIDGET_CREATE_CAMPAIGN: 'FINDEM_WIDGET_CREATE_CAMPAIGN',
  FINDEM_WIDGET_CALIBRATION_COMPLETED: 'FINDEM_WIDGET_CALIBRATION_COMPLETED',
};

// Store active message handler for cleanup
let activeMessageHandler = null;
let messageHandler = null;

export const createWidgetConfig = ({
  job,
  user,
  token,
  collectionId,
  view = 'calibration',
  vcInfo,
  vcUrl,
  jobBoardUrl,
}) => {
  let baseUrl = '';
  try {
    const url = import.meta.env.VITE_API_BASE_URL;
    if (!url) {
      baseUrl = '';
    } else {
      // Ensure new URL (in case there's a path in base URL)
      const { host, protocol } = new URL(url);
      baseUrl = host ? `${protocol}//${host}` : '';
    }
  } catch (e) {
    baseUrl = '';
  }

  return {
    agentId: view === 'calibration' ? FINDEM_CALIBRATION_AGENT_ID : FINDEM_BOOST_AGENT_ID,
    view,
    bearerToken: token,
    debug: true,
    autoOpen: false,
    displayMode: 'inline',
    uiConfig: {
      width: '100%',
      panelPosition: {
        bottom: 0,
        right: 0,
      },
      disableSlideAnimation: true,
      showHeader: false,
      inputPlaceholder: view === 'calibration' ? 'Message agent' : `Ask me anything and I'll be happy to help!`,
      emptyStateTitle: '',
      emptyStateSubtitle:
        view === 'calibration'
          ? "Hi! I'm here to help you identify the ideal candidate for your next hire."
          : `Hi 👋 I'm here to help you boost your job ${job?.title} at ${job?.organization?.name}${
              job?.sourcingCampaign?.calibrate ? '. Calibration has been completed.' : '.'
            }`,
      initialPrompts:
        view === 'calibration'
          ? ['Get started with my job description']
          : ['Start the boost 🚀', 'Let me check the campaign messaging first'],
    },
    agentParams: {
      job_description: job?.description || job?.title || 'No job description',
      icp_id: job?.sourcingCampaign?.icpId || 'icp_123',
      job_id: job?.id?.toString() || '',
      collection_id: collectionId || '',
      job_url: job?.url || '',
      job_title: job?.title || '',
      vc_info: vcInfo || '',
      vc_url: vcUrl || '',
      is_getro: true,
      job_board_base: jobBoardUrl ? addProtocolToUrl(jobBoardUrl) : '',
      base_url_input: baseUrl ?? '',
    },
    sessionMetadata: {
      source: 'admin_portal',
      user_id: user?.id || 'unknown',
      job_id: job?.id?.toString() || '',
      collection_id: collectionId || null,
      sourcing_campaign_id: job?.sourcingCampaign?.id || null,
    },
    containerId: 'findem-widget-container',
    sessionId:
      view === 'calibration'
        ? job?.sourcingCampaign?.calibrateChatSessionId
        : job?.sourcingCampaign?.outreachChatSessionId || null,
    notificationUrl: `${import.meta.env.VITE_API_V2_BASE_URL}/findem/webhook` || null,
  };
};

export const createDynamicFindemIframe = (config) => {
  const iframeId = 'findem-widget-iframe';

  return new Promise((resolve, reject) => {
    const container = document.getElementById('findem-widget-modal');
    if (!container) {
      reject(new Error('Container element is required'));
      return;
    }

    // Check if iframe already exists
    const existingIframe = document.getElementById(iframeId);
    if (existingIframe) {
      // If iframe already exists, destroy and recreate with new config
      if (existingIframe.parentNode) {
        existingIframe.parentNode.removeChild(existingIframe);
      }
    }

    // Create HTML content for iframe with config embedded
    const configJson = JSON.stringify(config).replace(/</g, '\\u003c').replace(/>/g, '\\u003e');
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <base href="/app/" />
        <title>Findem Widget</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body, html {
            width: 100%;
            height: 100%;
            overflow: hidden;
          }
          #findem-widget-container {
            width: 100%;
            height: 100%;
          }

        </style>
      </head>
      <body>
        <div id="findem-widget-container"></div>
        <script>

          // Set config synchronously before loading script (matching the example pattern)
          window.FindemWidgetConfig = ${configJson};

          // attach onReady callback to the config
          window.FindemWidgetConfig.onReady = () => {
            if (window.parent) {
              window.parent.postMessage({
                type: 'FINDEM_WIDGET_READY',
                success: true
              }, '*');
            }
          };
          window.FindemWidgetConfig.onOpen = () => {
            if (window.parent) {
              window.parent.postMessage({
                type: 'FINDEM_WIDGET_OPENED',
                success: true
              }, '*');
            }
          };
          window.FindemWidgetConfig.onError = (error) => {
            if (window.parent) {
              window.parent.postMessage({
                type: 'FINDEM_WIDGET_ERROR',
                error: error
              }, '*');
            }
          };
           window.FindemWidgetConfig.onMessage = (event, payload) => {
            if (event === 'calibration-completed') {
              if (window.parent) {
                window.parent.postMessage({
                  type: 'FINDEM_WIDGET_CALIBRATION_COMPLETED',
                  payload: {
                    job_id: window.FindemWidgetConfig.agentParams.job_id,
                    collection_id: window.FindemWidgetConfig.agentParams.collection_id,
                    view: window.FindemWidgetConfig.view,
                    sourcing_campaign_id: window.FindemWidgetConfig.sessionMetadata.sourcing_campaign_id,
                  },
                }, '*');
              }

              return;
            }

            if (window.parent) {
              window.parent.postMessage({
                type: payload?.boost_id ? 'FINDEM_WIDGET_CREATE_CAMPAIGN' : 'FINDEM_WIDGET_MESSAGE',
                payload: {
                  icp_id: payload.boost_id,
                  event,
                  session_id: window.FindemWidgetConfig.sessionId,
                  job_id: window.FindemWidgetConfig.agentParams.job_id,
                  collection_id: window.FindemWidgetConfig.agentParams.collection_id,
                  view: window.FindemWidgetConfig.view,
                  sourcing_campaign_id: window.FindemWidgetConfig.sessionMetadata.sourcing_campaign_id,
                },
              }, '*');
            }
          };

          window.FindemWidgetConfig.onSessionChange = (sessionId) => {
            if (window.parent) {
              window.parent.postMessage({
                type: 'FINDEM_WIDGET_SESSION_CHANGE',
                payload: {
                  sessionId,
                  job_id: window.FindemWidgetConfig.agentParams.job_id,
                  collection_id: window.FindemWidgetConfig.agentParams.collection_id,
                  view: window.FindemWidgetConfig.view,
                  sourcing_campaign_id: window.FindemWidgetConfig.sessionMetadata.sourcing_campaign_id,
                }
              }, '*');
            }
          };

          // Load the Findem widget script
          const script = document.createElement('script');
          script.src = 'https://chat-widget.findem.ai/embed.js';
          script.async = true;

          script.onload = function() {
            // Widget should initialize automatically using window.FindemWidgetConfig
            // Send ready message back to parent
            if (window.parent) {
              window.parent.postMessage({
                type: 'FINDEM_WIDGET_LOADED',
                success: true
              }, '*');
            }
          };

          script.onerror = function(error) {
            // Send error message back to parent
            if (window.parent) {
              window.parent.postMessage({
                type: 'FINDEM_WIDGET_ERROR',
                error: 'Failed to load Findem widget script'
              }, '*');
            }
          };

          document.head.appendChild(script);
        </script>
      </body>
      </html>
    `.trim();

    // Create blob URL from HTML content
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);

    // Create iframe element
    const iframe = document.createElement('iframe');
    iframe.id = iframeId;
    iframe.src = blobUrl;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.display = 'block';

    messageHandler = (event) => {
      if (event.data && event.data.type === 'FINDEM_WIDGET_READY') {
        activeMessageHandler = null;
        // Clean up blob URL
        URL.revokeObjectURL(blobUrl);
        resolve(config);
      } else if (event.data && event.data.type === 'FINDEM_WIDGET_ERROR') {
        activeMessageHandler = null;
        URL.revokeObjectURL(blobUrl);
        container.removeChild(iframe);
        reject(new Error(event.data.error || 'Failed to initialize Findem widget'));
      } else if (
        event.data &&
        [
          findemWidgetEventNames.FINDEM_WIDGET_CREATE_CAMPAIGN,
          findemWidgetEventNames.FINDEM_WIDGET_SESSION_CHANGE,
          findemWidgetEventNames.FINDEM_WIDGET_CALIBRATION_COMPLETED,
        ].includes(event.data.type)
      ) {
        dispatchEvent(
          new CustomEvent(event.data.type, {
            detail: event.data,
          }),
        );
      }
    };

    activeMessageHandler = messageHandler;
    window.addEventListener('message', messageHandler);

    // Handle iframe load errors
    iframe.onerror = () => {
      window.removeEventListener('message', messageHandler);
      activeMessageHandler = null;
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Failed to load Findem widget iframe'));
    };

    // Append iframe to container
    container?.appendChild(iframe);
  });
};

export const cleanupFindemWidget = () => {
  const iframeId = 'findem-widget-iframe';
  const iframe = document.getElementById(iframeId);

  if (messageHandler) {
    window.removeEventListener('message', messageHandler);
    messageHandler = null;
  }

  // Remove message event listener if it exists
  if (activeMessageHandler) {
    window.removeEventListener('message', activeMessageHandler);
    activeMessageHandler = null;
  }

  // Remove iframe from DOM
  if (iframe && iframe.parentNode) {
    iframe.parentNode.removeChild(iframe);
  }
};
